import React, { useEffect } from 'react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { LoadingIndicator } from './loading-indicator.component';
import CloseIcon from '@mui/icons-material/Close';
import { useAuthorize } from '../hooks/use-authorize';
import { UserPermissionName } from '../models/user-permission-name';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  titleForConfirmButton?: string;
  autoCloseDuration?: number;
  hideButtons?: boolean;
}

const ConfirmDialog = ({
  open,
  title,
  message,
  onClose,
  onConfirm,
  isLoading = false,
  titleForConfirmButton = '',
  autoCloseDuration,
  hideButtons = false,
}: ConfirmDialogProps) => {
  useEffect(() => {
    if (open && autoCloseDuration) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDuration, onClose]);

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog open={open} className="modalpopup" title={title} onClose={onClose}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <div style={{ padding: '25px 50px' }}>
        <div>{message}</div>
        {/* You can uncomment and pass the isLoading prop if you have a LoadingIndicator component */}
        {isLoading && <LoadingIndicator isLoading={isLoading} />}
      </div>
      {!hideButtons && (
        <DialogActionsBar>
          <div className="spaceBetween">
            <button
              style={{ cursor: 'pointer' }}
              className="btn btn-sm btn-default btn-lightGrey"
              onClick={onClose}
              autoFocus
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary btn-delete"
              style={{
                backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                color: isReadOnlyPermission ? 'black' : '#fff',
                cursor: 'pointer'
              }}
              type="button"
              onClick={onConfirm}
              disabled={isLoading || isReadOnlyPermission}
            >
              {!titleForConfirmButton ? 'Yes' : titleForConfirmButton}
            </button>
          </div>
        </DialogActionsBar>
      )}
    </Dialog>
  );
};

export default ConfirmDialog;
